import { useEffect, useMemo, useState } from 'react';
import { FilterContextData } from 'contexts/FilterContext';
import { useContext } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import fetchProfitPoolData from 'components/pages/ProfitPoolOverview/apiUtils';
import { PROFIT_POOL_API_LIMIT } from 'components/pages/ProfitPoolOverview/apiUtils';
import { isEmpty, omit, startCase } from 'lodash';
import { getWaterFallChartData } from 'components/pages/GrowthMarginDrivers/hooks/growthMarginDataUtils';
import { UserContext } from 'contexts/ConfigContext';
import { formatNumWithKMBAlt2, getConfig } from 'utils/helperMethods';

const useGrowthMarginBridgesData = () => {
    const selectedKPI = 'growth_margin_bridges';

    const { appConfig } = useContext(UserContext);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { growthBridgesKpiLabelMapping } = profitPoolConfig;

    const [profitGrowthDriversData, setProfitGrowthDriversData] = useState<any[]>([]);
    const [previousYearGMBridgesData, setPreviousYearGMBridgesData] = useState<number>(0);
    const [currentYearGMBridgesData, setCurrentYearGMBridgesData] = useState<number>(0);
    const [cardsData, setCardsData] = useState<any[]>([]);

    const {
        idForRestOfComponent,
        timePeriodSelection,
        singleSelectedTier,
        allDimAndAttributeTypeList,
        timePeriodLoading,
    } = useContext(FilterContextData);

    // Get WaterFallChartData

    const {
        data: attributeProfitData,
        isPending: isAttributeProfitPending,
        isError: isAttributeProfitError,
        isSuccess: isAttributeProfitSuccess,
    } = useQuery({
        queryKey: ['profit-growth-drivers', selectedKPI, singleSelectedTier, idForRestOfComponent, timePeriodSelection],
        queryFn: ({ signal }) => {
            return fetchProfitPoolData({
                kpi: selectedKPI,
                tierList: singleSelectedTier,
                yearAgo: false,
                groupedByList: [],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
            });
        },
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                timePeriodSelection,
        ),
    });

    useEffect(() => {
        if (!isAttributeProfitPending && attributeProfitData) {
            const allData = attributeProfitData?.data[0];
            const currentYearGMBridgesData = allData?.kpi_values?.['current_year_profit'] || 0;
            setCurrentYearGMBridgesData(currentYearGMBridgesData);
            const updatedFinalChartData = [];

            const previousYearGMData = allData?.kpi_values?.['year_ago_profit'] || 0;
            setPreviousYearGMBridgesData(previousYearGMData);

            setCardsData([
                {
                    metricName: 'Base',
                    absoluteValue: previousYearGMData,
                },
                {
                    metricName: 'Final',
                    absoluteValue: currentYearGMBridgesData,
                },
                {
                    metricName: 'Growth',
                    absoluteValue: currentYearGMBridgesData - previousYearGMData,
                },
                {
                    metricName: 'Growth %',
                    absoluteValue: isFinite(
                        ((currentYearGMBridgesData - previousYearGMData) / previousYearGMData) * 100,
                    )
                        ? ((currentYearGMBridgesData - previousYearGMData) / previousYearGMData) * 100
                        : null,
                    unit: '%',
                },
            ]);

            if (!isEmpty(allData)) {
                for (const [key, value] of Object.entries(
                    omit(allData?.kpi_values, ['year_ago_profit', 'current_year_profit']),
                )) {
                    updatedFinalChartData.push({
                        kpiName: growthBridgesKpiLabelMapping?.[key] || startCase(key),
                        kpiValue: value,
                        kpiKey: key,
                        [key]: value,
                        tier: allData.tier,
                    });
                }

                setProfitGrowthDriversData(updatedFinalChartData);
            } else {
                setProfitGrowthDriversData([]);
            }
        }
    }, [isAttributeProfitPending, attributeProfitData]);

    // Memoize waterfall chart data to avoid recalculating multiple times
    const waterfallChartResult = useMemo(() => {
        return getWaterFallChartData(
            profitGrowthDriversData,
            previousYearGMBridgesData,
            currentYearGMBridgesData,
            'kpiValue',
        );
    }, [profitGrowthDriversData, currentYearGMBridgesData, selectedKPI, previousYearGMBridgesData]);

    // Prepare Excel export data from the original data (before waterfall transform)
    const excelExportData = useMemo(() => {
        if (!profitGrowthDriversData?.length) {
            return { headers: [], data: [] };
        }

        const data = [];

        const headers = ['Name', 'Value', 'Trend'];

        data.push([
            'Previous Year',
            formatNumWithKMBAlt2(previousYearGMBridgesData, 2, 2) || 0,
            previousYearGMBridgesData > 0 ? 'positive' : 'negative',
        ]);
        profitGrowthDriversData.map((item: any) => {
            data.push([
                item.kpiName || '',
                formatNumWithKMBAlt2(item.kpiValue, 2, 2) || 0,
                Number(item.kpiValue) >= 0 ? 'positive' : 'negative',
            ]);
        });

        data.push([
            'Current Year',
            formatNumWithKMBAlt2(currentYearGMBridgesData, 2, 2) || 0,
            currentYearGMBridgesData > 0 ? 'positive' : 'negative',
        ]);

        return { headers, data };
    }, [profitGrowthDriversData]);

    return {
        data: profitGrowthDriversData,
        currentYearData: currentYearGMBridgesData,
        previousYearData: previousYearGMBridgesData,
        waterFallChartData: waterfallChartResult?.chartData,
        min: waterfallChartResult?.min,
        max: waterfallChartResult?.max,
        cardsData: cardsData,
        excelExportData,
        isPending: isAttributeProfitPending,
        isError: isAttributeProfitError,
        isSuccess: isAttributeProfitSuccess,
    };
};

export default useGrowthMarginBridgesData;
